<template>
  <div :class="[{ 'owc-highlight--dark': isDarkMode }, 'owc-highlight']">
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <SlotContainer
      refName="slot-media"
      class="owc-highlight__media"
      :slot-component-data="slotData"
      v-test-id.ignore="'media'"
    >
      <slot></slot>
    </SlotContainer>
    <div class="wb-grid-container" v-if="sideNote || description">
      <div class="wb-grid-row">
        <div
          v-if="description"
          class="wb-grid-col-mq1-12 wb-grid-col-mq3-8 owc-highlight__description"
          v-html="description"
          v-test-id.ignore="'description'"
        ></div>
        <div
          v-if="sideNote"
          v-html="sideNote"
          v-test-id.ignore="'sidenote'"
          class="wb-grid-col-mq1-12 wb-grid-col-mq3-2 wb-grid-col-offset-mq3-1 owc-highlight__sidenote"
        ></div>
      </div>
    </div>
    <div v-if="footnotes" class="wb-grid-container owc-highlight__footer">
      <globalelem-footnote-component
        :module-id="moduleId"
        :theme="isDarkMode ? 'dark' : 'light'"
      ></globalelem-footnote-component>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Highlight Component
 * The Highlight purpose is to highlight important features of the car.
 * It contains a slot where it can go a GenericImage, VideoPlayer or a HighlightSlider
 * By default the module should be placed after a Headline and it can have dark and light mode
 * ValidationBox is available for this component using the slot-name 'validation-box'
 * Footnotes component is placed in the bottom of the highlight slider, containing any information
 * placed in the module and its childrens.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { TestId } from '@owc-utils/TestIdDirective';
import { HighlightComponent } from './Highlight';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { HIGHLIGHT_TRACKING_EVENT_NAME } from './HighlightUtils';
import { getLocalFootnotes, setModuleId } from '@owc-utils/Footnotes/FootnotesHelper';

export default (ComponentInitializeDispatcher as HighlightComponent).extend({
  name: 'owc-highlight',
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  directives: {
    TestId,
  },
  mixins: [OwcDataComponent],
  mounted() {
    this.$el.addEventListener(HIGHLIGHT_TRACKING_EVENT_NAME, this.handleTracking as EventListener, {
      once: true,
    });
  },
  computed: {
    description: function() {
      return this.componentData?.description;
    },
    sideNote: function() {
      return this.componentData?.sideNote;
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
    slotData: function() {
      return JSON.stringify({
        darkMode: this.isDarkMode,
      });
    },
    footnotes() {
      return getLocalFootnotes(this.componentData);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    handleTracking(): void {
      this.handleCustomTracking(this.$el, 'highlight_slider', 'feature', 'click');
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rte';

.owc-highlight {
  @include wb-type-copy-secondary;
  padding-bottom: var(--wb-spacing-xl);

  &__media {
    margin-bottom: var(--wb-spacing-s);

    @include breakpoint-from(mq6) {
      @include wb-content-width;
    }
  }
  &__description,
  &__sidenote {
    margin-bottom: var(--wb-spacing-m);

    color: var(--wb-grey-20);
  }
  &__description {
    @include breakpoint-to(mq2) {
      margin-bottom: var(--wb-spacing-l);
    }
  }
  &--dark {
    color: var(--wb-white);

    background-color: var(--wb-black);

    .owc-highlight__description,
    .owc-highlight__sidenote {
      color: var(--wb-white);
    }
  }
}
.owc-highlight-slider {
  &__slide {
    position: absolute;

    opacity: 0;

    &--isActive {
      opacity: 1;
    }
  }
}
</style>
